import { ref, computed, watch, onMounted, type Ref, type ComputedRef, type WatchSource, unref } from "vue";

export interface UsePreloadedInfiniteScrollOptions<T> {
  /**
   * Async function to fetch a page given pageParam (0-indexed by default).
   */
  fetchPage: (pageParam: number) => Promise<T[]>;
  /**
   * Expected page size. If page returned has fewer items than pageSize,
   * it is marked as the last page. Default: 20
   */
  pageSize?: number;
  /**
   * Number of future pages to preload ahead in the background.
   * Default: 2
   */
  preloadAhead?: number;
  /**
   * Initial page param index. Default: 0
   */
  initialPageParam?: number;
  /**
   * Optional custom check for whether a page is the terminal/last page.
   */
  isLastPage?: (pageData: T[], pageParam: number) => boolean;
  /**
   * Optional item ID extractor for deduplication.
   * Defaults to extracting `item.id`.
   */
  getItemId?: (item: T) => string | number;
  /**
   * Optional callback when a page finishes loading (initial, preloaded, or fetched on demand).
   * Useful for pre-caching images, analytics, etc.
   */
  onPageLoaded?: (pageData: T[], pageParam: number, isPreload: boolean) => void;
  /**
   * Reactive sources to watch. When changed, the feed resets and refetches from the beginning.
   */
  watchSources?: WatchSource<any> | Array<WatchSource<any>> | (() => any);
  /**
   * Optional callback called right before resetting and refetching.
   */
  onReset?: () => void;
  /**
   * Whether to fetch the initial page immediately on setup.
   * Default: true
   */
  immediate?: boolean;
  /**
   * Optional cache key to retain displayed pages in memory across page navigations.
   */
  cacheKey?: string | Ref<string | undefined> | ComputedRef<string | undefined> | (() => string | undefined);
}

export interface UsePreloadedInfiniteScrollReturn<T> {
  /** Flattened and deduplicated array of all currently displayed items */
  items: ComputedRef<T[]>;
  /** Array of displayed page arrays */
  displayedPages: Ref<T[][]>;
  /** True while the initial page is loading */
  isLoading: Ref<boolean>;
  /** True when user reaches end of feed and the next page is currently being awaited */
  isFetchingNextPage: Ref<boolean>;
  /** True if more pages exist either in the preload buffer or on the server */
  hasNextPage: ComputedRef<boolean>;
  /** Error object if fetching failed */
  error: Ref<Error | null>;
  /** Call to append the next preloaded page (or fetch it if not ready yet) */
  fetchNextPage: () => Promise<void>;
  /** Reset state and refetch from initial page */
  reset: () => Promise<void>;
}

interface CacheEntry<T> {
  displayedPages: T[][];
  pageBuffer: Map<number, Promise<T[]> | T[]>;
  lastKnownPageIndex: number | null;
}

const globalScrollCache = new Map<string, CacheEntry<any>>();

/**
 * A reusable composable for infinite scrolling with background multi-page preloading.
 *
 * Flow:
 * - Page 1 is displayed → preloads Page 2 and Page 3 in the background.
 * - User reaches the end of Page 1 → instantly appends Page 2, and preloads Page 4 (so Page 3 and Page 4 are ready).
 * - User reaches the end of Page 2 → instantly appends Page 3, and preloads Page 5.
 * - Continues this pattern until no more pages exist.
 */
export function usePreloadedInfiniteScroll<T = any>(
  options: UsePreloadedInfiniteScrollOptions<T>
): UsePreloadedInfiniteScrollReturn<T> {
  const pageSize = options.pageSize ?? 20;
  const preloadAhead = options.preloadAhead ?? 3;
  const initialPageParam = options.initialPageParam ?? 0;

  const resolveCacheKey = (): string | undefined => {
    if (!options.cacheKey) return undefined;
    if (typeof options.cacheKey === "function") {
      return (options.cacheKey as () => string | undefined)();
    }
    return unref(options.cacheKey);
  };

  const initialKey = resolveCacheKey();
  const initialCached = initialKey ? globalScrollCache.get(initialKey) : undefined;
  const hasInitialCache = Boolean(initialCached && initialCached.displayedPages.length > 0);

  const displayedPages = ref<T[][]>(
    hasInitialCache && initialCached ? initialCached.displayedPages.map((p) => [...p]) : []
  ) as Ref<T[][]>;

  const isLoading = ref<boolean>(hasInitialCache ? false : options.immediate !== false);
  const isFetchingNextPage = ref<boolean>(false);
  const error = ref<Error | null>(null);

  // Map of pageParam -> Promise<T[]> (in-flight) or T[] (resolved)
  const pageBuffer = new Map<number, Promise<T[]> | T[]>(
    hasInitialCache && initialCached ? initialCached.pageBuffer : []
  );

  // Tracks the 0-indexed page index of the last available page containing items from the server
  const lastKnownPageIndex = ref<number | null>(
    hasInitialCache && initialCached ? initialCached.lastKnownPageIndex : null
  );

  // Monotonic epoch counter to cancel / ignore out-of-order responses on reset
  let currentEpoch = 0;

  const syncToCache = () => {
    const key = resolveCacheKey();
    if (!key) return;
    globalScrollCache.set(key, {
      displayedPages: displayedPages.value,
      pageBuffer: new Map(pageBuffer),
      lastKnownPageIndex: lastKnownPageIndex.value,
    });
  };

  const getItemKey = (item: T): string | number => {
    if (options.getItemId) return options.getItemId(item);
    if (item && typeof item === "object" && "id" in item) {
      return (item as any).id;
    }
    return JSON.stringify(item);
  };

  const items = computed<T[]>(() => {
    const flattened = displayedPages.value.flat();
    const seen = new Set<string | number>();
    const result: T[] = [];
    for (const item of flattened) {
      if (item === null || item === undefined) continue;
      const key = getItemKey(item);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(item);
      }
    }
    return result;
  });

  const hasNextPage = computed<boolean>(() => {
    if (isLoading.value) return false;
    // If we haven't encountered a terminal page yet, there are more potential pages
    if (lastKnownPageIndex.value === null) return true;
    // If we know the terminal page index, check if all pages up to it have been displayed
    const currentDisplayedIndex = initialPageParam + displayedPages.value.length - 1;
    return currentDisplayedIndex < lastKnownPageIndex.value;
  });

  const isTerminalPage = (pageData: T[], pageIndex: number): boolean => {
    if (options.isLastPage) {
      return options.isLastPage(pageData, pageIndex);
    }
    return !pageData || pageData.length < pageSize;
  };

  /**
   * Fetch a single page and store in buffer.
   */
  const fetchSinglePage = (
    pageIndex: number,
    epoch: number,
    isPreload: boolean
  ): Promise<T[]> => {
    if (lastKnownPageIndex.value !== null && pageIndex > lastKnownPageIndex.value) {
      return Promise.resolve([]);
    }

    if (pageBuffer.has(pageIndex)) {
      const existing = pageBuffer.get(pageIndex)!;
      return existing instanceof Promise ? existing : Promise.resolve(existing);
    }

    const fetchPromise = (async () => {
      try {
        const data = await options.fetchPage(pageIndex);
        if (epoch !== currentEpoch) return [];

        const pageData = Array.isArray(data) ? data : [];

        if (pageData.length === 0) {
          const terminalIndex = Math.max(initialPageParam - 1, pageIndex - 1);
          if (lastKnownPageIndex.value === null || terminalIndex < lastKnownPageIndex.value) {
            lastKnownPageIndex.value = terminalIndex;
          }
        } else if (isTerminalPage(pageData, pageIndex)) {
          if (lastKnownPageIndex.value === null || pageIndex < lastKnownPageIndex.value) {
            lastKnownPageIndex.value = pageIndex;
          }
        }

        pageBuffer.set(pageIndex, pageData);
        syncToCache();

        if (options.onPageLoaded) {
          try {
            options.onPageLoaded(pageData, pageIndex, isPreload);
          } catch (e) {
            console.error("[usePreloadedInfiniteScroll] onPageLoaded error:", e);
          }
        }

        return pageData;
      } catch (err: any) {
        if (epoch === currentEpoch) {
          pageBuffer.delete(pageIndex);
          if (!isPreload) {
            error.value = err instanceof Error ? err : new Error(String(err));
          }
        }
        throw err;
      }
    })();

    pageBuffer.set(pageIndex, fetchPromise);
    return fetchPromise;
  };

  /**
   * Preloads future pages ahead of the currently displayed pages.
   * e.g. If displayedPages has Page 1 (count = 1), preloads Page 2 and Page 3.
   * If displayedPages has Page 1 and 2 (count = 2), preloads Page 3 and Page 4.
   */
  const maintainPreloadBuffer = (epoch: number) => {
    if (epoch !== currentEpoch) return;

    const displayedCount = displayedPages.value.length;
    const startPreloadIndex = initialPageParam + displayedCount;
    const endPreloadIndex = startPreloadIndex + preloadAhead - 1;

    for (let targetIndex = startPreloadIndex; targetIndex <= endPreloadIndex; targetIndex++) {
      if (lastKnownPageIndex.value !== null && targetIndex > lastKnownPageIndex.value) {
        break;
      }
      if (!pageBuffer.has(targetIndex)) {
        fetchSinglePage(targetIndex, epoch, true).catch(() => {
          // Errors during background preload are swallowed here;
          // they will be retried if the user attempts to scroll to that page.
        });
      }
    }
  };

  /**
   * Appends the next page to displayedPages.
   * If the page is already preloaded in pageBuffer, appends immediately (instant transition).
   * Otherwise waits for the in-flight request or initiates a new fetch.
   */
  const fetchNextPage = async (): Promise<void> => {
    if (isLoading.value || isFetchingNextPage.value || !hasNextPage.value) {
      return;
    }

    const epoch = currentEpoch;
    const nextPageIndex = initialPageParam + displayedPages.value.length;

    if (lastKnownPageIndex.value !== null && nextPageIndex > lastKnownPageIndex.value) {
      return;
    }

    const buffered = pageBuffer.get(nextPageIndex);

    if (buffered && !(buffered instanceof Promise)) {
      // Data is already preloaded in memory -> append immediately!
      if (buffered.length > 0) {
        displayedPages.value.push(buffered);
        syncToCache();
      }
      maintainPreloadBuffer(epoch);
      return;
    }

    try {
      isFetchingNextPage.value = true;
      let data: T[];
      if (buffered instanceof Promise) {
        data = await buffered;
      } else {
        data = await fetchSinglePage(nextPageIndex, epoch, false);
      }

      if (epoch !== currentEpoch) return;

      if (data && data.length > 0) {
        displayedPages.value.push(data);
        syncToCache();
      }
      maintainPreloadBuffer(epoch);
    } catch (err: any) {
      if (epoch === currentEpoch) {
        error.value = err instanceof Error ? err : new Error(String(err));
      }
    } finally {
      if (epoch === currentEpoch) {
        isFetchingNextPage.value = false;
      }
    }
  };

  /**
   * Resets the buffer, displayed pages, and fetches page 1 fresh.
   */
  const reset = async (): Promise<void> => {
    const key = resolveCacheKey();
    if (key) {
      globalScrollCache.delete(key);
    }
    const epoch = ++currentEpoch;
    options.onReset?.();

    displayedPages.value = [];
    pageBuffer.clear();
    lastKnownPageIndex.value = null;
    error.value = null;
    isLoading.value = true;
    isFetchingNextPage.value = false;

    try {
      const firstPage = await fetchSinglePage(initialPageParam, epoch, false);
      if (epoch !== currentEpoch) return;

      if (firstPage && firstPage.length > 0) {
        displayedPages.value = [firstPage];
      } else {
        displayedPages.value = [];
      }
      syncToCache();

      // Page 1 is displayed → preload Page 2 and Page 3 in the background
      maintainPreloadBuffer(epoch);
    } catch (err: any) {
      if (epoch === currentEpoch) {
        error.value = err instanceof Error ? err : new Error(String(err));
      }
    } finally {
      if (epoch === currentEpoch) {
        isLoading.value = false;
      }
    }
  };

  if (options.watchSources) {
    watch(
      options.watchSources,
      () => {
        reset();
      },
      { deep: true }
    );
  }

  if (options.immediate !== false) {
    onMounted(() => {
      const key = resolveCacheKey();
      const cached = key ? globalScrollCache.get(key) : undefined;
      if (cached && cached.displayedPages.length > 0) {
        // Cache exists and is already loaded in displayedPages
        maintainPreloadBuffer(currentEpoch);
      } else {
        reset();
      }
    });
  }

  return {
    items,
    displayedPages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    reset,
  };
}
